import React, { useState, useEffect, useCallback } from 'react'
import { Search } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Input, Label, Badge, Card, CardContent,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui'
import { getAllPlayersWithGroups, getCargosByJugador, registrarPago, uploadComprobante, getSaldoFavorTotal, crearSaldoFavor, aplicarSaldoFavorACargo, getSaldoFavor } from '../../../lib/supabase'
import { supabase } from '../../../lib/supabaseClient'
import { useOrganization } from '../../../contexts/OrganizationContext'
import { isVisiblePlayer } from '../../../lib/playerVisibility'
import { formatDate, formatMoney } from '../../../utils/helpers'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { useInscritosTemporada } from '../../../hooks/useInscritosTemporada'
import { FiltroInscritos, AvisoSinPadron } from '../FiltroInscritos'

const RegistrarPagoModal = ({ open, onOpenChange, onSuccess, temporadaId = null }) => {
  const { organization } = useOrganization()
  const inscritosEstado = useInscritosTemporada(temporadaId)
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [step, setStep] = useState(1) // 1=buscar jugador, 2=seleccionar cargos, 3=confirmar
  const [players, setPlayers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [cargos, setCargos] = useState([])
  const [loadingCargos, setLoadingCargos] = useState(false)
  const [selectedCargos, setSelectedCargos] = useState({}) // { cargoId: montoAplicado }
  const [form, setForm] = useState({
    monto: '',
    metodo_pago: 'efectivo',
    referencia: '',
    notas: '',
  })
  const [comprobante, setComprobante] = useState(null)
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState(null)
  const [saldoFavorTotal, setSaldoFavorTotal] = useState(0)
  const [usarSaldoFavor, setUsarSaldoFavor] = useState(false)
  const [pagoSinConcepto, setPagoSinConcepto] = useState(false)

  useEffect(() => {
    if (open) {
      setStep(1)
      setSelectedPlayer(null)
      setCargos([])
      setSelectedCargos({})
      setForm({ monto: '', metodo_pago: 'efectivo', referencia: '', notas: '' })
      setComprobante(null)
      setSearchTerm('')
      setErrorMsg(null)
      setSaldoFavorTotal(0)
      setUsarSaldoFavor(false)
      setPagoSinConcepto(false)
      guard.resetDirty()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load players
  useEffect(() => {
    if (!open) return
    const loadPlayers = async () => {
      const { data } = await getAllPlayersWithGroups()
      setPlayers((data || []).filter(isVisiblePlayer))
    }
    loadPlayers()
  }, [open])

  // Solo los inscritos en la temporada del contexto: registrar un pago contra
  // alguien que no participa este ciclo es como se cuelan los cargos fantasma.
  const filteredPlayers = inscritosEstado.filtrar(players).filter((p) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return p.nombre_completo?.toLowerCase().includes(term) ||
      p.numero?.toString().includes(term) ||
      p.nickname?.toLowerCase().includes(term)
  }).slice(0, 20)

  const selectPlayer = useCallback(async (player) => {
    setSelectedPlayer(player)
    setLoadingCargos(true)
    // Acotado a la temporada del contexto: sin esto salian los cargos de TODAS
    // las temporadas y se podia pagar un cargo de otro ciclo sin darse cuenta.
    const [cargosRes, saldoRes] = await Promise.all([
      getCargosByJugador(player.id, temporadaId),
      getSaldoFavorTotal(player.id),
    ])
    const pendientes = (cargosRes.data || []).filter((c) => c.estatus !== 'pagado' && c.estatus !== 'cancelado')

    // Detectar cargos con solicitudes pendientes del papa para mostrar warning
    const cargoIds = pendientes.map((c) => c.id)
    let cargosConSolicitud = pendientes
    if (cargoIds.length > 0) {
      const { data: solCargos } = await supabase
        .from('pagos_solicitudes_cargos')
        .select('cargo_id, solicitud_id, pagos_solicitudes_pago!inner(estatus)')
        .in('cargo_id', cargoIds)
        .eq('pagos_solicitudes_pago.estatus', 'pendiente')
      const solByCargo = {}
      ;(solCargos || []).forEach((sc) => { solByCargo[sc.cargo_id] = sc.solicitud_id })
      cargosConSolicitud = pendientes.map((c) => ({ ...c, solicitud_pendiente_id: solByCargo[c.id] }))
    }
    setCargos(cargosConSolicitud)
    setSaldoFavorTotal(saldoRes.total || 0)
    setPagoSinConcepto(false)
    setUsarSaldoFavor(false)
    setLoadingCargos(false)
    setStep(2)
  }, [])

  const toggleCargo = (cargo) => {
    guard.markDirty()
    setSelectedCargos((prev) => {
      const copy = { ...prev }
      if (copy[cargo.id]) {
        delete copy[cargo.id]
      } else {
        copy[cargo.id] = Number(cargo.saldo)
      }
      return copy
    })
  }

  const totalSeleccionado = Object.values(selectedCargos).reduce((sum, v) => sum + v, 0)

  useEffect(() => {
    if (totalSeleccionado > 0) {
      setForm((p) => ({ ...p, monto: totalSeleccionado.toFixed(2) }))
    }
  }, [totalSeleccionado])

  const handleSubmit = async () => {
    if (!selectedPlayer || !form.monto) return
    setSaving(true)

    try {
      let comprobantePath = null
      if (comprobante) {
        const { path } = await uploadComprobante(organization?.id, comprobante)
        comprobantePath = path
      }

      if (pagoSinConcepto) {
        // Pago sin concepto → registrar transacción + crear saldo a favor
        const { error, data: txData } = await registrarPago({
          jugadorId: selectedPlayer.id,
          monto: Number(form.monto),
          metodoPago: form.metodo_pago,
          referencia: form.referencia || null,
          comprobantePath,
          cargosAplicar: [],
          notas: form.notas || 'Pago a cuenta (saldo a favor)',
        })
        if (error) throw error

        const saldoResult = await crearSaldoFavor({
          jugadorId: selectedPlayer.id,
          monto: Number(form.monto),
          origen: 'pago_sin_concepto',
          descripcion: `Pago a cuenta — ${form.metodo_pago}${form.referencia ? ` (${form.referencia})` : ''}`,
          transaccionOrigenId: txData?.id || null,
        })
        if (saldoResult.error) {
          console.error('[RegistrarPago] Error creando saldo a favor:', saldoResult.error)
          throw new Error('El pago se registró pero no se pudo crear el saldo a favor: ' + (saldoResult.error.message || ''))
        }
      } else {
        // Pago normal con cargos seleccionados
        const cargosAplicar = Object.entries(selectedCargos).map(([cargoId, montoAplicado]) => ({
          cargo_id: cargoId,
          monto_aplicado: montoAplicado,
        }))

        const { error } = await registrarPago({
          jugadorId: selectedPlayer.id,
          monto: Number(form.monto),
          metodoPago: form.metodo_pago,
          referencia: form.referencia || null,
          comprobantePath,
          cargosAplicar,
          notas: form.notas || null,
        })
        if (error) throw error

        // Auto-rechazar solicitudes pendientes de los cargos que se pagaron manualmente
        const cargoIdsPagados = Object.keys(selectedCargos).filter((id) => parseFloat(selectedCargos[id]) > 0)
        if (cargoIdsPagados.length > 0) {
          const { data: solicitudesAfectadas } = await supabase
            .from('pagos_solicitudes_cargos')
            .select('solicitud_id, pagos_solicitudes_pago!inner(estatus)')
            .in('cargo_id', cargoIdsPagados)
            .eq('pagos_solicitudes_pago.estatus', 'pendiente')

          const solicitudIds = [...new Set((solicitudesAfectadas || []).map((s) => s.solicitud_id))]
          if (solicitudIds.length > 0) {
            await supabase
              .from('pagos_solicitudes_pago')
              .update({
                estatus: 'rechazada',
                rechazo_razon: 'Pagado por otro medio (admin registró pago manual)',
                revisada_en: new Date().toISOString(),
              })
              .in('id', solicitudIds)
          }
        }
      }

      setSaving(false)
      guard.resetDirty()
      onSuccess?.()
    } catch (err) {
      setSaving(false)
      setErrorMsg(err.message || 'Error al registrar el pago')
    }
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 1 && 'Registrar Pago — Seleccionar Jugador'}
            {step === 2 && `Registrar Pago — ${selectedPlayer?.nombre_completo}`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar jugador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
            <AvisoSinPadron estado={inscritosEstado} />
            <FiltroInscritos estado={inscritosEstado} id="rp-ver-no-inscritos" />
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {filteredPlayers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-muted flex items-center gap-2"
                  onClick={() => selectPlayer(p)}
                >
                  <span className="font-medium text-sm">{p.nombre_completo}</span>
                  {p.numero && <Badge variant="outline" className="text-xs">#{p.numero}</Badge>}
                </button>
              ))}
              {filteredPlayers.length === 0 && (
                <p className="text-muted-foreground text-sm text-center py-4">
                  {inscritosEstado.filtrando
                    ? 'Ningún inscrito coincide. Marca "Ver no inscritos" si buscas a alguien fuera de esta temporada.'
                    : 'No se encontraron jugadores'}
                </p>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-xs">
              ← Cambiar jugador
            </Button>

            {/* Saldo a favor disponible */}
            {saldoFavorTotal > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-800">Saldo a favor disponible</p>
                  <p className="text-xs text-emerald-600">Se puede aplicar a cargos pendientes</p>
                </div>
                <span className="text-lg font-bold text-emerald-700">{formatMoney(saldoFavorTotal)}</span>
              </div>
            )}

            {/* Toggle: pago a cargos vs pago sin concepto */}
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              <Button
                type="button"
                variant={!pagoSinConcepto ? 'default' : 'ghost'}
                size="sm"
                className="flex-1 h-8 text-xs"
                onClick={() => { setPagoSinConcepto(false); setSelectedCargos({}); setForm((p) => ({ ...p, monto: '' })) }}
              >
                Pagar cargos
              </Button>
              <Button
                type="button"
                variant={pagoSinConcepto ? 'default' : 'ghost'}
                size="sm"
                className="flex-1 h-8 text-xs"
                onClick={() => { setPagoSinConcepto(true); setSelectedCargos({}); setForm((p) => ({ ...p, monto: '' })) }}
              >
                Pago a cuenta
              </Button>
            </div>

            {pagoSinConcepto ? (
              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
                El monto se registrará como saldo a favor. Después podrás aplicarlo a cargos específicos.
              </p>
            ) : loadingCargos ? (
              <p className="text-muted-foreground text-sm text-center py-4">Cargando cargos...</p>
            ) : cargos.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No hay cargos pendientes para este jugador</p>
            ) : (
              <div className="space-y-1">
                <Label className="text-sm font-medium">Seleccionar cargos a pagar</Label>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {cargos.map((c) => {
                    const selected = !!selectedCargos[c.id]
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleCargo(c)}
                        className={`w-full text-left px-3 py-2 rounded-md border text-sm flex items-center justify-between ${selected ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                      >
                        <div className="min-w-0">
                          <span>{c.pagos_conceptos?.nombre || 'Cargo'}</span>
                          {c.pagos_planes?.nombre && (
                            <p className="text-[10px] text-muted-foreground truncate">{c.pagos_planes.nombre}</p>
                          )}
                          {c.solicitud_pendiente_id && (
                            <p className="text-[10px] text-amber-700 mt-1">
                              ⚠ Este cargo tiene solicitud pendiente del papá. Al guardar se rechazará automáticamente.
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {c.fecha_vencimiento && (
                            <span className="text-xs text-muted-foreground">{formatDate(c.fecha_vencimiento)}</span>
                          )}
                          <Badge variant={c.estatus === 'vencido' ? 'destructive' : 'secondary'} className="text-xs">
                            {formatMoney(c.saldo)}
                          </Badge>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Campos de pago — siempre visibles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">Monto</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.monto}
                  onChange={(e) => setForm((p) => ({ ...p, monto: e.target.value }))}
                  placeholder="$0.00"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Método de pago</Label>
                <Select value={form.metodo_pago} onValueChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, metodo_pago: v })) }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Referencia</Label>
              <Input
                value={form.referencia}
                onChange={(e) => setForm((p) => ({ ...p, referencia: e.target.value }))}
                placeholder="No. de transferencia, recibo, etc."
              />
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Comprobante (opcional)</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setComprobante(e.target.files?.[0] || null)}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-sm">Notas</Label>
              <Input
                value={form.notas}
                onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
                placeholder="Opcional"
              />
            </div>

            {errorMsg && (
              <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">{errorMsg}</p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
              <Button
                onClick={handleSubmit}
                disabled={saving || !form.monto || (!pagoSinConcepto && Object.keys(selectedCargos).length === 0)}
              >
                {saving ? 'Registrando...' : pagoSinConcepto ? `Registrar a cuenta ${formatMoney(form.monto)}` : `Registrar ${formatMoney(form.monto)}`}
              </Button>
            </DialogFooter>
          </div>
        )}
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default RegistrarPagoModal
